import React, { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { PLAYBACK_POLL_MS } from '@/constants/contentFeed';
import {
  loadYouTubeIframeApi,
  YOUTUBE_PLAYER_STATE,
  type YouTubePlayer,
  type YouTubeStateChangeEvent,
} from '@/modules/content/creation/model/youtube';

type Props = {
  videoId: string;
  title?: string;
  // Fires on genuine play/pause transitions. BUFFERING is reported as *not* playing —
  // a buffering video is on screen but not playing, and counting it would recreate the
  // exact bug this component exists to fix.
  onPlayStateChange?: (isPlaying: boolean) => void;
  onProgress?: (currentTimeSeconds: number, durationSeconds: number) => void;
};

// A YouTube embed mounted through the IFrame API rather than a bare `<iframe src=embed>`.
//
// The bare embed is simpler and is still used everywhere playback does not need to be
// observed (the detail page). But it is a black box: it reports nothing, so a caller
// cannot distinguish "the user watched this" from "this was on screen". The loop needs
// that distinction to report honest view telemetry, so it pays the cost of the API.
const YouTubeVideo = ({ videoId, title, onPlayStateChange, onProgress }: Props) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const pollRef = useRef<number | null>(null);

  // Held in refs so changing callbacks never tear down and remount the player mid-watch.
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const onProgressRef = useRef(onProgress);
  onPlayStateChangeRef.current = onPlayStateChange;
  onProgressRef.current = onProgress;

  useEffect(() => {
    let isCurrent = true;

    const stopPolling = () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const startPolling = (player: YouTubePlayer) => {
      stopPolling();
      pollRef.current = window.setInterval(() => {
        const duration = player.getDuration?.() ?? 0;
        const current = player.getCurrentTime?.() ?? 0;
        if (duration > 0) onProgressRef.current?.(current, duration);
      }, PLAYBACK_POLL_MS);
    };

    loadYouTubeIframeApi()
      .then(() => {
        if (!isCurrent || !hostRef.current || !window.YT?.Player) return;

        playerRef.current = new window.YT.Player(hostRef.current, {
          videoId,
          playerVars: {
            // Muted so the browser will allow programmatic playback at all; the viewer
            // unmutes through the player's own controls.
            mute: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady: () => {},
            onError: () => {},
            onStateChange: (event: YouTubeStateChangeEvent) => {
              const isPlaying = event.data === YOUTUBE_PLAYER_STATE.PLAYING;
              onPlayStateChangeRef.current?.(isPlaying);

              if (isPlaying) startPolling(event.target);
              else stopPolling();
            },
          },
        });
      })
      .catch(() => {
        // The embed simply will not appear; nothing is reported, which is the correct
        // failure mode — better a missing view than an invented one.
      });

    return () => {
      isCurrent = false;
      stopPolling();
      // Leaving the item counts as stopping playback.
      onPlayStateChangeRef.current?.(false);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [videoId]);

  // YT.Player replaces this node with its iframe, so the wrapper carries the sizing.
  return (
    <Box width="100%" height="100%" sx={{ '& iframe': { width: '100%', height: '100%', border: 'none' } }}>
      <Box ref={hostRef} width="100%" height="100%" title={title} />
    </Box>
  );
};

export default YouTubeVideo;
